import { compile } from 'path-to-regexp';

import { sanitizeUrl } from '@xing-com/crate-people-directory-utils';
import { IconArrowLeft, IconArrowRight } from '@xing-com/icons';

import {
  Container,
  ArrowContainer,
  PaginationLabelsContainer,
  PaginationLabel,
  PaginationLink,
} from './pagination.styles';

type PaginationProps = {
  currentPage: number;
  pagesNumber: number;
  route: string;
  routeFirstPage?: string;
  routeParams: Record<string, string | number>;
  filterQuery?: Record<string, string>;
};

type PaginationElement = {
  page: number | string;
  isActive: boolean;
  url?: string;
};

export const Pagination: React.FC<PaginationProps> = ({
  currentPage,
  pagesNumber,
  route,
  routeFirstPage,
  routeParams,
  filterQuery,
}) => {
  const parsedPagesNumber = +pagesNumber;
  const parsedCurrentPage = +currentPage;

  const pathWithSearch = (
    routeTemplate: string,
    params: Record<string, string | number>
  ): string => {
    const normalizedParams = Object.fromEntries(
      Object.entries(params).map(([key, value]) => [key, String(value)])
    );

    let path = compile(routeTemplate)(normalizedParams);
    if (filterQuery && Object.keys(filterQuery).length > 0) {
      path += '?' + new URLSearchParams(filterQuery).toString();
    }
    return sanitizeUrl(path);
  };

  routeFirstPage = routeFirstPage || route;
  // Format: [1, ..., parsedCurrentPage - 1, parsedCurrentPage, parsedCurrentPage + 1, ..., lastPage]
  let elements: PaginationElement[] = [
    {
      page: 1,
      isActive: parsedCurrentPage === 1,
      url: pathWithSearch(routeFirstPage, {
        ...routeParams,
        page: 1,
      }),
    },
  ];

  if (parsedCurrentPage - 2 > 1) {
    elements.push({ page: '...', isActive: false });
  }

  [parsedCurrentPage - 1, parsedCurrentPage, parsedCurrentPage + 1].forEach(
    (page) => {
      if (page > 1 && page < parsedPagesNumber) {
        elements.push({
          page,
          isActive: parsedCurrentPage === page,
          url: pathWithSearch(route, {
            ...routeParams,
            page: page,
          }),
        });
      }
    }
  );

  if (parsedCurrentPage + 2 < parsedPagesNumber) {
    elements.push({ page: '...', isActive: false });
  }

  elements = [
    ...elements,
    {
      page: parsedPagesNumber,
      isActive: parsedCurrentPage === parsedPagesNumber,
      url: pathWithSearch(route, {
        ...routeParams,
        page: parsedPagesNumber,
      }),
    },
  ];

  const leftArrowRoute = parsedCurrentPage === 2 ? routeFirstPage : route;

  return (
    <Container>
      <ArrowContainer
        $isDisabled={parsedCurrentPage <= 1}
        to={pathWithSearch(leftArrowRoute, {
          ...routeParams,
          page: parsedCurrentPage - 1,
        })}
      >
        <IconArrowLeft />
      </ArrowContainer>
      <PaginationLabelsContainer>
        {elements.map(({ page, isActive, url }, index) => {
          return url ? (
            <PaginationLink
              key={`${page}-${index}`}
              $isActive={isActive}
              to={url}
            >
              {page}
            </PaginationLink>
          ) : (
            <PaginationLabel key={`${page}-${index}`}>{page}</PaginationLabel>
          );
        })}
      </PaginationLabelsContainer>
      <ArrowContainer
        $isDisabled={parsedCurrentPage >= parsedPagesNumber}
        to={pathWithSearch(route, {
          ...routeParams,
          page: parsedCurrentPage + 1,
        })}
      >
        <IconArrowRight />
      </ArrowContainer>
    </Container>
  );
};
